// Verifies one rendition manifest against the root manifest's own copy of
// its identity, per docs/plans/2026-08-13-verification-integration-closeout.md,
// T14. Two checks, both required:
//
//   (a) the fetched bytes hash to RenditionRef.manifest_hash — the same
//       raw-bytes discipline verifyRoot.ts established (hash before decode)
//   (b) the fetched manifest's own name/resolution/codecs/bandwidth/
//       segment_count agree with the RenditionRef's copies of those fields
//
// (b) exists because (a) alone only proves "these bytes are what the root
// committed to at manifest_hash" — it says nothing about whether they were
// fetched for and describe the rendition the caller actually asked about.
// See api/pkg/verification/v2/manifest.go:68: without this cross-check the
// segment lists of two quality levels named in the same root are
// interchangeable — a hash-valid response served under the wrong name would
// otherwise pass. A RenditionManifest whose bytes hash correctly but whose
// own fields describe a different rendition must fail this check; that is
// the case __tests__/verifyRendition.test.ts exists to pin.
import { API_BASE_URL } from '../constants.js'
import { hexEqual, sha256Hex } from './hash.js'
import type { RenditionRef } from './verifyRoot.js'

// Hand-mirrored from api/pkg/verification/v2/manifest.go's SegmentEntry /
// RenditionManifest, the same convention verifyRoot.ts's RootManifest uses:
// snake_case because this is a straight JSON.parse of wire bytes, not a
// generated/renamed type, so ./stack generate_types does not cover it.
export interface SegmentEntry {
  path: string
  hash: string
  size: number
  duration: number
}

export interface RenditionManifest {
  schema: string
  hash_algo: string
  name: string
  resolution: string
  codecs: string
  bandwidth: number
  average_bandwidth: number
  playlist_path: string
  segment_count: number
  total_duration: number
  segments: SegmentEntry[]
}

export interface RenditionVerified {
  ok: true
  manifest: RenditionManifest
  /** The computed hash, lowercase hex. */
  hash: string
}

export interface RenditionVerificationFailure {
  ok: false
  stage: 'rendition'
  /** Same discriminant T13 established for root.json: a network problem
   * must never be reported (or acted on) as a mismatch. See verifyRoot.ts. */
  kind: 'network' | 'mismatch'
  detail: string
  /** The rendition name being verified, present on every failure so a caller
   * juggling several renditions (T15) can tell which one failed without
   * parsing `detail`. */
  rendition: string
  /** Present only when kind === 'mismatch' and the failure was a hash
   * disagreement (not an identity-field disagreement). */
  expectedHash?: string
  /** Present only when kind === 'mismatch' and the failure was a hash
   * disagreement (not an identity-field disagreement). */
  actualHash?: string
}

export type VerifyRenditionResult = RenditionVerified | RenditionVerificationFailure

/**
 * Fetches the rendition manifest named by `ref.name`, hashes it, and checks
 * it against the root's RenditionRef for that same rendition. Never throws
 * into the render path — every failure comes back as a structured
 * `RenditionVerificationFailure` with `stage: 'rendition'`.
 */
export async function verifyRendition(videoId: string, ref: RenditionRef): Promise<VerifyRenditionResult> {
  let bytes: ArrayBuffer
  try {
    const response = await fetch(`${API_BASE_URL}/videos/${videoId}/verification/renditions/${ref.name}`)
    if (!response.ok) {
      return {
        ok: false,
        stage: 'rendition',
        kind: 'network',
        detail: `rendition "${ref.name}" manifest request failed: HTTP ${response.status}`,
        rendition: ref.name,
      }
    }
    // Hashed before anything decodes it — see hash.ts and verifyRoot.ts for
    // why a parse/re-stringify round trip must never feed sha256Hex.
    bytes = await response.arrayBuffer()
  } catch (err) {
    return {
      ok: false,
      stage: 'rendition',
      kind: 'network',
      detail: err instanceof Error ? err.message : 'Could not reach the API',
      rendition: ref.name,
    }
  }

  const actualHash = await sha256Hex(bytes)
  if (!hexEqual(actualHash, ref.manifest_hash)) {
    return {
      ok: false,
      stage: 'rendition',
      kind: 'mismatch',
      detail: `rendition "${ref.name}" manifest does not match the hash sealed on chain`,
      rendition: ref.name,
      expectedHash: ref.manifest_hash,
      actualHash,
    }
  }

  let manifest: RenditionManifest
  try {
    manifest = JSON.parse(new TextDecoder().decode(bytes)) as RenditionManifest
  } catch {
    // Bytes matched the sealed hash but are not valid JSON — same "something
    // is wrong upstream, not evidence the seal broke" reasoning verifyRoot.ts
    // uses, hence 'network' rather than 'mismatch'.
    return {
      ok: false,
      stage: 'rendition',
      kind: 'network',
      detail: `rendition "${ref.name}" manifest matched its hash but could not be parsed as JSON`,
      rendition: ref.name,
    }
  }

  const identityDisagreement = describeIdentityDisagreement(manifest, ref)
  if (identityDisagreement) {
    return {
      ok: false,
      stage: 'rendition',
      kind: 'mismatch',
      detail: `rendition "${ref.name}" manifest ${identityDisagreement}`,
      rendition: ref.name,
    }
  }

  return { ok: true, manifest, hash: actualHash }
}

// Returns a human-readable description of the first field that disagrees, or
// null when every identity field matches. Checked in the fixed order the
// fields are listed in this plan's T14 acceptance criteria.
function describeIdentityDisagreement(manifest: RenditionManifest, ref: RenditionRef): string | null {
  if (manifest.name !== ref.name) {
    return `describes name "${manifest.name}", expected "${ref.name}"`
  }
  if (manifest.resolution !== ref.resolution) {
    return `describes resolution "${manifest.resolution}", expected "${ref.resolution}"`
  }
  if (manifest.codecs !== ref.codecs) {
    return `describes codecs "${manifest.codecs}", expected "${ref.codecs}"`
  }
  if (manifest.bandwidth !== ref.bandwidth) {
    return `describes bandwidth ${manifest.bandwidth}, expected ${ref.bandwidth}`
  }
  if (manifest.segment_count !== ref.segment_count) {
    return `describes segment_count ${manifest.segment_count}, expected ${ref.segment_count}`
  }
  return null
}
